/**
 * Shared utilities for {@link ActivityRow}, {@link ActivityCard} and {@link ActivitiesDataTable}.
 */
import { ACTIVITY_STATUS_CONFIG, type ActivityFilters } from './activity-configs';
import type { Activity } from '@/types/activity.types';

/**
 * Resolves the display name of an activity's type,
 * falling back through activityTypeName → activityType.label → null.
 */
export function getActivityTypeName(activity: Activity): string | null {
  return activity.activityTypeName || activity.activityType?.label || null;
}

/**
 * Returns the status config for an activity.
 * Shared between {@link ActivityCard} and {@link ActivityRow} to avoid the duplicate lookup.
 */
export function getActivityStatusConfig(activity: Activity) {
  return ACTIVITY_STATUS_CONFIG[activity.status];
}

/** Dashboard / export: show em dash when no cap is set. */
export function formatActivityMaxCell(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

/** Map UI filters + pagination to activity list params. */
export function toActivityListParams(
  filters: ActivityFilters,
  page: number,
  pageSize: number,
): {
  page: number;
  pageSize: number;
  statuses?: ActivityFilters['statuses'];
  activityTypeIds?: string[];
  search?: string;
} {
  return {
    page,
    pageSize,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    activityTypeIds: filters.activityTypeIds.length > 0 ? filters.activityTypeIds : undefined,
    search: filters.searchQuery,
  };
}

/** True when any status / type / search filter is set. */
export function hasActivityActiveFilters(filters: ActivityFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.activityTypeIds.length > 0 ||
    !!filters.searchQuery?.trim()
  );
}
